import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CalendarDays, CheckCircle2, Clock, CreditCard, Loader2, Lock, Video } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { SlotPicker } from "@/components/MentorBookingCard";

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

function Step({ n, title, done, children }) {
  return (
    <div className={`rounded-2xl border bg-white p-6 ${done ? "border-emerald-200" : "border-slate-200"}`}>
      <div className="flex items-center gap-2 font-display font-bold text-lg text-slate-900">
        {done ? <CheckCircle2 size={18} className="text-emerald-600" /> : <span className="w-6 h-6 rounded-full bg-blue-900 text-white text-xs flex items-center justify-center">{n}</span>}
        {title}
      </div>
      <div className="mt-3 text-sm text-slate-600">{children}</div>
    </div>
  );
}

function PayOptions({ offer, onPay, busy, t }) {
  const opts = [
    ["full", t("residencyOffer.payFull", { amount: usd(offer.amount) })],
    ...(offer.allow_pay_later ? [["later", t("residencyOffer.payLater", { amount: usd(offer.amount) })]] : []),
  ];
  return (
    <div className="grid gap-2">
      {opts.map(([k, label]) => (
        <Button key={k} disabled={!!busy} onClick={() => onPay(k)} data-testid={`residency-pay-${k}`}
          className={`h-11 justify-start ${k === "full" ? "bg-blue-900 hover:bg-blue-800" : "bg-white text-slate-900 border border-slate-300 hover:bg-slate-50"}`}>
          {busy === k ? <Loader2 size={14} className="mr-2 animate-spin" /> : <CreditCard size={14} className="mr-2" />} {label}
        </Button>
      ))}
      {offer.allow_pay_later && <p className="text-xs text-slate-500" data-testid="residency-pay-later-hint">{t("residencyOffer.payLaterHint")}</p>}
    </div>
  );
}

export default function ResidencyOffer() {
  const { appId } = useParams();
  const { t, i18n } = useTranslation();
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [picker, setPicker] = useState(false);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => api.get(`/residency/offer/${appId}`).then((r) => setD(r.data))
    .catch((e) => setErr({ status: e.response?.status, msg: e.response?.data?.detail })), [appId]);
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { load(); return; }
    let tries = 0;
    const poll = () => api.get(`/shop/status/${sid}`).then((r) => {
      if (r.data.payment_status === "paid" || r.data.status === "paid" || ++tries > 8) { setParams({}); load(); } else setTimeout(poll, 2000);
    }).catch(() => { setParams({}); load(); });
    poll();
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps
  const fmt = (iso) => new Date(iso).toLocaleString(i18n.language || "en", { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" });

  if (err) return (
    <div data-testid="residency-offer-error" className="max-w-xl mx-auto rounded-2xl border border-slate-200 bg-white p-8 text-center">
      <Lock className="mx-auto text-amber-600" />
      <p className="mt-3 text-slate-700">{err.msg || t("residencyOffer.notFound")}</p>
    </div>
  );
  if (!d) return <Loader2 className="animate-spin text-slate-400 mx-auto mt-10" />;

  const bookIntake = async (start) => {
    await api.post(`/residency/offer/${appId}/intake`, { start });
    toast.success(t("residencyOffer.intakeBooked"));
    load();
  };
  const pay = async (option) => {
    setBusy(option);
    try { const { data } = await api.post(`/residency/offer/${appId}/pay`, { option }); window.location.href = data.url; }
    catch (e) { toast.error(e.response?.data?.detail || t("mentorBooking.error")); setBusy(""); }
  };
  const o = d.offer;
  const paid = o?.paid === "paid";

  return (
    <div data-testid="residency-offer-page" className="max-w-2xl mx-auto space-y-5">
      <div>
        <div className="text-xs uppercase tracking-widest text-amber-700 font-semibold">{t("residencyOffer.eyebrow")}</div>
        <h1 className="font-display font-black text-4xl text-slate-900 tracking-tight mt-1">{d.program_name}</h1>
      </div>
      <Step n={1} title={t("residencyOffer.step1")} done={!!d.intake}>
        {d.intake ? (
          <div data-testid="residency-intake-booked" className="space-y-1">
            <div className="font-semibold text-slate-900 flex items-center gap-1.5"><CalendarDays size={14} /> {fmt(d.intake.start)}</div>
            {d.intake.meeting_link && <a href={d.intake.meeting_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-800 font-semibold underline"><Video size={13} /> {t("mentorBooking.joinLink")}</a>}
          </div>
        ) : (
          <>
            <p>{t("residencyOffer.step1Body")}</p>
            <Button className="mt-3 bg-blue-900 hover:bg-blue-800" onClick={() => setPicker(true)} data-testid="residency-book-intake"><CalendarDays size={14} className="mr-2" /> {t("residencyOffer.pickTime")}</Button>
          </>
        )}
      </Step>
      <Step n={2} title={t("residencyOffer.step2")} done={paid}>
        {paid ? <p data-testid="residency-paid" className="text-emerald-700 font-semibold">{t("residencyOffer.enrolled")}</p>
          : !o ? <p data-testid="residency-no-link">{t("residencyOffer.step2Waiting")}</p>
          : o.expired ? <p data-testid="residency-link-expired" className="text-red-600 font-semibold">{t("residencyOffer.expired")}</p>
          : (
            <div className="space-y-3">
              <p className="flex items-center gap-1.5 text-amber-700 font-semibold" data-testid="residency-link-expires"><Clock size={14} /> {t("residencyOffer.expiresAt", { when: fmt(o.expires_at) })}</p>
              <PayOptions offer={o} onPay={pay} busy={busy} t={t} />
            </div>
          )}
      </Step>
      <SlotPicker open={picker} onOpenChange={setPicker} onConfirm={bookIntake} title={t("residencyOffer.intakeTitle")} confirmKey="residencyOffer.confirmIntake" showHold={false} showNote={false} />
    </div>
  );
}
